import { useMutation } from 'data-fetching-client';
import { useState } from 'react';
import Cancelled from '../../components/import/Cancelled';
import Cancelling from '../../components/import/Cancelling';
import Importing from '../../components/import/Importing';
import Scraping from '../../components/import/Scraping';
import SuccessfullyCompleted from '../../components/import/SuccessfullyCompleted';
import ConfirmCancelImportArticlesModal from '../../components/modals/ConfirmCancelImportArticlesModal';
import { useSmartCopyProgress } from '../../importV3/hooks/useSmartCopyProgress';
import { useSmartCopyWarnings } from '../../importV3/hooks/useSmartCopyWarnings';
import { CANCEL_SMART_COPY } from '../../importV3/queries';
import { selectKnowledgeBaseId } from '../../knowledge-base/selectors/knowledgeBaseDetails';
import { useAppSelector } from '../../utils/hooks';
import { Flex } from 'foundations-components/components/Flex';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function ImportArticlesPanel() {
  const knowledgeBaseId = useAppSelector(selectKnowledgeBaseId);
  const canWriteArticle = useAppSelector(state => state.knowledgeScopes.canWriteArticle);
  const progress = useSmartCopyProgress(knowledgeBaseId);
  const warnings = useSmartCopyWarnings(knowledgeBaseId);
  const [showProgress, setShowProgress] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [cancelSmartCopy, {
    loading: isCanceling
  }] = useMutation(CANCEL_SMART_COPY, {
    variables: {
      knowledgeBaseId
    }
  });
  if (progress.state === 'none' || !showProgress) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    marginBlockEnd: "500",
    paddingInline: "300",
    borderColor: "border-core-subtle",
    borderWidth: "100",
    borderStyle: "solid",
    "data-test-id": "import-articles-panel",
    children: [renderInner(), showModal && /*#__PURE__*/_jsx(ConfirmCancelImportArticlesModal, {
      isRequesting: isCanceling,
      onClose: () => setShowModal(false),
      onCancelImportArticles: cancelSmartCopy
    }), progress.state === 'CANCELLED' || progress.state === 'COMPLETED' && /*#__PURE__*/_jsx(CloseButton, {
      onClick: () => setShowProgress(false),
      color: "text-core-subtle"
    })]
  });
  function renderInner() {
    switch (progress.state) {
      case 'SCRAPING':
        return /*#__PURE__*/_jsx(Scraping, {});
      case 'IMPORTING':
        return /*#__PURE__*/_jsx(Importing, {
          canWriteArticle: canWriteArticle,
          importedArticleCount: progress.importedArticleCount,
          onCancelClick: () => setShowModal(true),
          progress: calculateProgress(progress),
          totalArticleCount: progress.totalArticleCount
        });
      case 'COMPLETED':
        {
          return warnings.isFetched ? /*#__PURE__*/_jsx(SuccessfullyCompleted, {
            alteredArticlesListLink: warnings,
            autoPublishEnabled: progress.autoPublishEnabled,
            count: warnings.count,
            importedArticleCount: progress.importedArticleCount,
            progress: calculateProgress(progress),
            skippedArticleCount: 0,
            totalArticleCount: progress.totalArticleCount
          }) : /*#__PURE__*/_jsx(Importing, {
            canWriteArticle: canWriteArticle,
            importedArticleCount: progress.importedArticleCount,
            isCancelable: false,
            onCancelClick: () => {},
            progress: calculateProgress(progress),
            totalArticleCount: progress.totalArticleCount
          });
        }
      case 'CANCELLING':
        return /*#__PURE__*/_jsx(Cancelling, {});
      case 'CANCELLED':
        {
          return warnings.isFetched ? /*#__PURE__*/_jsx(Cancelled, {
            alteredArticlesListLink: warnings,
            autoPublishEnabled: progress.autoPublishEnabled,
            count: warnings.count,
            importedArticleCount: progress.importedArticleCount,
            totalArticleCount: progress.totalArticleCount
          }) : /*#__PURE__*/_jsx(Cancelling, {});
        }
      default:
        return null;
    }
  }
}
export default ImportArticlesPanel;
function calculateProgress({
  importedArticleCount,
  skippedArticleCount,
  totalArticleCount
}) {
  return Math.round(Math.min((importedArticleCount + skippedArticleCount) / totalArticleCount, 1) * 100);
}